// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { of, Subscription } from 'rxjs';
import { catchError, subscribeOn } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { ResponseBaseService } from './response-base.service';
import { QuestionsBaseService } from '..';
import { environment } from '../../../../environments/environment';
import {
  SurveyResponseModel,
  ApplicationError,
  PatchModel,
  QuestionResponseModel,
  ContextQuestionModel,
  Constants,
  SurveyStatuses
} from '../../../models';
import { AppService } from '../app.service';
import { PatchOperations } from 'src/app/models/common/patch-operations';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ResponseService extends ResponseBaseService implements OnDestroy {
  private subs: Subscription;
  private _isLoading: boolean;
  private _surveyIdentifier: string;

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private questionsService: QuestionsBaseService,
    private translate: TranslateService
  ) {
    super(questionsService);

    this._isLoading = false;
    this._surveyIdentifier = '';
  }

  ngOnDestroy() {
    if (this.subs != null) {
      this.subs.unsubscribe();
      this.subs = null;
    }
  }

  loadResponse(surveyIdentifier: string): void {
    if (this._isLoading) {
      return;
    }

    if (
      !surveyIdentifier ||
      (surveyIdentifier === this._surveyIdentifier &&
        this._activeResponse$.value)
    ) {
      const surveyResponse = Object.assign(
        {} as SurveyResponseModel,
        this._activeResponse$.value
      );
      const questionResponses = this._activeResponse$.value && [
        ...this._activeResponse$.value.questionResponses
      ];
      surveyResponse.questionResponses = questionResponses;
      this._activeResponse$.next(surveyResponse);
      this.initialiseQuestionResponses(surveyResponse.questionResponses);

      return;
    }

    this._isLoading = true;
    this._surveyIdentifier = surveyIdentifier;

    const url =
      environment.apiConfig.baseUrl +
      environment.apiConfig.responseApi +
      `/${surveyIdentifier}`;

    this.http
      .get<SurveyResponseModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(surveyResponse => {
        if (surveyResponse) {
          this._isLoading = false;
          this._activeResponse$.next(surveyResponse);
          this.initialiseQuestionResponses(surveyResponse.questionResponses);
          this.updateProgress();
        }
      });
  }

  updateStatus(surveyResponseModel: SurveyResponseModel): void {
    if (!surveyResponseModel) {
      return;
    }

    const patchModel = {
      operation: PatchOperations.replace,
      path: 'status',
      value: surveyResponseModel.status.toString()
    } as PatchModel;
    const url =
      environment.apiConfig.baseUrl +
      environment.apiConfig.responseApi +
      `/${surveyResponseModel.id}`;
    this.http
      .patch<SurveyResponseModel>(url, patchModel)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        response.questionResponses =
          this._activeResponse$.value &&
          this._activeResponse$.value.questionResponses;
        this._activeResponse$.next(response);
        this._surveyIdentifier = null;
      });
  }

  updateComment(name: string, value: string): void {
    if (!value) {
      return;
    }

    const activeResponse = this._activeResponse$.value;
    if (!activeResponse) {
      return;
    }

    const url = environment.apiConfig.baseUrl + environment.apiConfig.responseApi + `/${activeResponse.id}`;
    const model = {
      operation: PatchOperations.replace,
      path: name,
      value
    } as PatchModel;
    this.http.patch<SurveyResponseModel>(url, model).pipe(

    ).subscribe(response => {
      activeResponse[name] = response[name];
      this._activeResponse$.next(activeResponse);
    });
  }

  questionAnswered(response: ContextQuestionModel): void {
    if (!response) {
      return;
    }

    const url =
      environment.apiConfig.baseUrl +
      environment.apiConfig.questionResponseApi +
      `/${response.questionResponseId}`;
    this.http
      .put<QuestionResponseModel>(url, response)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._questionModelError.bind(this))
      )
      .subscribe(model => {
        if (!model) {
          response.response = null;
          return;
        }

        const updated =
          this._activeResponse$.value &&
          this._activeResponse$.value.questionResponses.find(
            qr => qr.id === model.id
          );
        updated.response = model.response;
        this.updateProgress();
      });
  }

  update(
    model: import('../../../models').SurveyResponseModel
  ): import('rxjs').Observable<boolean> {
    throw new Error('Method not implemented.');
  }

  onNext(response: SurveyResponseModel): void {
    const activeResponse = this._activeResponse$.value;
    if (activeResponse) {
      this.initialiseQuestionResponses(activeResponse.questionResponses);

      if (this._groupIndex$.value > 4) {
        response.status = SurveyStatuses.effectivenessQuestions;
        this.updateStatus(response);
      }
    }
  }

  private _modelError(err) {
    this._handleError(err);
    this._isLoading = false;
    return of(null as SurveyResponseModel);
  }

  private _questionModelError(err) {
    this._handleError(err);
    this._isLoading = false;
    return of(null as QuestionResponseModel);
  }

  private _handleError(err) {
    this.appService.setLoading(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);
  }
}
